/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import { useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormInputProvider from '../../Context/FormInput';
import { getErrorMessage } from '../../Helpers/validation';
import { setWasSeen } from '../../Lib/redux/slices/components';
import AutoCompleteInput from '../AutoCompleteInput/AutoCompleteInput';
import CheckboxInput from '../CheckboxInput/CheckboxInput';
import DatePicker from '../DatePicker/DatePicker';
import ExternalSelect from '../ExternalSelect/ExternalSelect';
import GoogleAddressInput from '../GoogleAddressInput/GoogleAddressInput';
import Hint from '../Hint/Hint';
import Html from '../Html/Html';
import Loader from '../Loader/Loader';
import Newsletter from '../Newsletter/Newsletter';
import PhoneNumberInput from '../PhoneNumberInput/PhoneNumberInput';
import QuestionText from '../QuestionText/QuestionText';
import RadioInput from '../RadioInput/RadioInput';
import Select from '../Select/Select';
import SupplierMatch from '../SupplierMatch/SupplierMatch';
import TextFieldInput from '../TextFieldInput/TextFieldInput';
import {
  getComponentValidationStatus,
  hasPhoneConstraint,
  isExternalSelect,
} from '../__helpers__/component.helpers';
import makeFormElementStyles from './makeFormElementStyles';
import { ComponentTypes } from '../../Data/Components';
import SupplierCard from '../SupplierCard/SupplierCard';
import SelectAndConnect from '../SelectAndConnect/SelectAndConnect';
import { renderMustache } from '../../Helpers/mustache';

const FormElementComponent = ({
  autoAdvance,
  componentId,
  iFrameId,
  isVisible,
  slideDirection,
  subheading,
  text,
  type,
  value,
  headerPresent,
  wasSeen,
  duration,
  additionalSettings,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const styling = makeFormElementStyles({ theme, isVisible });
  // The calculations below for the heightOfLoaderContainer need to be done
  // before mounting the child component <Loader/> to prevent bad UX
  const formHeaderHeight = headerPresent ? 75 : 0;
  const progressBarHeight = 20;
  // The height below depends on the size and text settings set in loader.jsx
  const heightOfLoaderComponents = 153.016;
  // The height setting below ensures that the loader components are centred
  // within the area under the widget progress bar at all times
  const heightOfLoaderContainer = `calc(0.5 * (100vh - ${
    formHeaderHeight + progressBarHeight + heightOfLoaderComponents
  }px) + ${heightOfLoaderComponents}px)`;

  let formElement;

  const components = useSelector((i) => i.components);
  const formId = useSelector((store) => store.formConfig.formId);
  const supplierMatches = useSelector((store) => store.supplierMatches);
  const showBrandMatchCount = useSelector(
    (store) => store.features.showBrandMatchCount
  );
  const component = components[`component_${componentId}`];
  const mappedQuestionId = component?.component?.mappedQuestionId;

  // TODO we should get the component here from state, not in InputArea, as this is where the input component is constructed. Pass only the componentId or unrelated app values to FormElement, then depending on the type of the component get the props that the component needs in a large switch statement. i.e. trying to make the input components are presentational as possible thus less re-rendering

  switch (type) {
    case ComponentTypes.AUTO_COMPLETE:
      formElement = (
        <AutoCompleteInput
          componentId={componentId}
          iFrameId={iFrameId}
          subheading={subheading}
          text={text}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.RADIOS:
      formElement = (
        <RadioInput
          autoAdvance={autoAdvance}
          componentId={componentId}
          iFrameId={iFrameId}
          subheading={subheading}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.DATEPICKER:
      formElement = (
        <DatePicker
          componentId={componentId}
          subheading={subheading}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.HINT:
      formElement = (
        <Hint
          componentId={componentId}
          title={renderMustache({
            template: additionalSettings?.title,
            components,
            currentComponent: component,
            iFrameId,
            formId,
            supplierMatches,
            showBrandMatchCount,
          })}
          content={renderMustache({
            template: additionalSettings?.content,
            components,
            currentComponent: component,
            iFrameId,
            formId,
            supplierMatches,
            showBrandMatchCount,
          })}
          iconUrl={additionalSettings?.icon_url}
        />
      );
      break;
    case ComponentTypes.HTML:
      formElement = (
        <Html
          componentId={componentId}
          text={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
            supplierMatches,
            showBrandMatchCount,
          })}
        />
      );
      break;
    case ComponentTypes.TEXT:
      formElement = (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
            supplierMatches,
            showBrandMatchCount,
          })}
        />
      );
      break;
    case ComponentTypes.GOOGLE_ADDRESS:
      formElement = (
        <GoogleAddressInput
          componentId={componentId}
          iFrameId={iFrameId}
          text={text}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.LOADER:
      formElement = (
        <Loader
          slideDirection={slideDirection}
          text={text}
          height={heightOfLoaderContainer}
          duration={duration}
          componentId={componentId}
        />
      );

      break;
    case ComponentTypes.TEXTFIELD:
      if (
        hasPhoneConstraint({
          component,
        })
      ) {
        const componentStatus = getComponentValidationStatus({ component });

        const { displayQuestionTextInsideTextfield } = component.component;
        const { placeholder, isAutoFilledActive } = component.input;
        const errorMessage = getErrorMessage(component.validation?.constraints);

        formElement = (
          <PhoneNumberInput
            componentId={componentId}
            displayQuestionTextInsideTextfield={
              displayQuestionTextInsideTextfield
            }
            errorMessage={errorMessage}
            iFrameId={iFrameId}
            componentStatus={componentStatus}
            placeholder={placeholder}
            subheading={subheading}
            text={text}
            value={value}
            isAutoFilledActive={isAutoFilledActive}
            formId={formId}
          />
        );
      } else {
        formElement = (
          <TextFieldInput
            componentId={componentId}
            iFrameId={iFrameId}
            text={text}
            subheading={subheading}
            value={value}
            formId={formId}
          />
        );
      }
      break;

    case ComponentTypes.TEXTAREA:
      formElement = (
        <TextFieldInput
          componentId={componentId}
          iFrameId={iFrameId}
          text={text}
          subheading={subheading}
          value={value}
          multiline
          rows={6}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.SELECT:
      if (isExternalSelect(components, mappedQuestionId)) {
        formElement = (
          <ExternalSelect
            autoAdvance={autoAdvance}
            componentId={componentId}
            iFrameId={iFrameId}
            subheading={subheading}
            formId={formId}
          />
        );
      } else {
        formElement = (
          <Select
            autoAdvance={autoAdvance}
            componentId={componentId}
            iFrameId={iFrameId}
            subheading={subheading}
            formId={formId}
          />
        );
      }
      break;
    case ComponentTypes.CHECKBOXES:
      formElement = (
        <CheckboxInput
          componentId={componentId}
          iFrameId={iFrameId}
          subheading={subheading}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.SELECT_AND_CONNECT:
      formElement = (
        <SelectAndConnect
          componentId={componentId}
          iFrameId={iFrameId}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.SUPPLIER_CARD:
      formElement = <SupplierCard componentId={componentId} />;
      break;
    case ComponentTypes.SUPPLIER_MATCH:
      formElement = (
        <SupplierMatch
          componentId={componentId}
          iFrameId={iFrameId}
          subheading={subheading}
          formId={formId}
        />
      );
      break;
    case ComponentTypes.NEWSLETTER_SIGNUP:
      formElement = (
        <Newsletter
          componentId={componentId}
          iFrameId={iFrameId}
          formId={formId}
        />
      );
      break;

    default:
      break;
  }

  useEffect(() => {
    if (!wasSeen && isVisible) {
      dispatch(setWasSeen({ componentId, value: true }));
    }
  }, [isVisible]);

  // Checkboxes and radios have a tile view that can expand beyond the regular width of the form
  // Only apply this max width wrapper to the other components
  if (type !== ComponentTypes.CHECKBOXES && type !== ComponentTypes.RADIOS) {
    formElement = <div css={styling.maxWidthWrapper}>{formElement}</div>;
  }

  return (
    <div id={componentId} data-visible={isVisible}>
      {type === ComponentTypes.LOADER && formElement}
      {type !== ComponentTypes.LOADER && (
        <FormInputProvider autoAdvance={autoAdvance}>
          <div css={styling.component}>{formElement}</div>
        </FormInputProvider>
      )}
    </div>
  );
};

const FormElement = memo(FormElementComponent);

export default FormElement;
